/**
 * Construit l'URL complète d'une photo de membre.
 * Les photos sont stockées dans /uploads/photos/ et servies directement
 * par Nginx (location /uploads/), pas par /api/uploads/.
 * @param {string} photoUrl - Le chemin relatif stocké en DB (ex: /uploads/photos/xxx.png)
 * @returns {string|null} L'URL absolue ou null si pas de photo
 */
export const getPhotoUrl = (photoUrl) => {
  if (!photoUrl) return null;
  // REACT_APP_API_URL = 'https://bonepeyantoko.org/api'
  // On supprime le suffixe /api pour obtenir la racine du site
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
  return `${baseUrl}${photoUrl}`;
};

/**
 * @param {number} montant - Le montant à formater
 * @returns {string} Le montant formaté
 */
export const formatMontant = (montant) => {
  if (montant == null) return '0 FCFA';
  return `${Number(montant).toLocaleString('fr-FR')} FCFA`;
};

/**
 * Formate une date au format français
 * @param {string|Date} date - La date à formater
 * @param {string} format - Le format souhaité (par défaut: DD/MM/YYYY)
 * @returns {string} La date formatée
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '-';
  const moment = require('moment');
  return moment(date).format(format);
};

/**
 * Formate un numéro de téléphone pour WhatsApp
 * @param {string} telephone - Le numéro de téléphone
 * @returns {string} Le numéro formaté pour WhatsApp
 */
export const formatPhoneForWhatsApp = (telephone) => {
  if (!telephone) return '';
  // Enlever tous les caractères non numériques
  let cleaned = telephone.replace(/\D/g, '');
  
  // Ajouter le code pays si absent (237 pour le Cameroun)
  if (!cleaned.startsWith('237')) {
    cleaned = '237' + cleaned;
  }
  
  return cleaned;
};

/**
 * Génère un lien WhatsApp avec message pré-rempli
 * @param {string} telephone - Le numéro de téléphone
 * @param {string} message - Le message à envoyer
 * @returns {string} L'URL WhatsApp
 */
export const generateWhatsAppLink = (telephone, message = '') => {
  const formattedPhone = formatPhoneForWhatsApp(telephone);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}${message ? `?text=${encodedMessage}` : ''}`;
};

/**
 * Calcule le pourcentage
 * @param {number} valeur - La valeur
 * @param {number} total - Le total
 * @returns {number} Le pourcentage
 */
export const calculatePercentage = (valeur, total) => {
  if (total === 0) return 0;
  return Math.round((valeur / total) * 100);
};

/**
 * Tronque un texte avec ellipsis
 * @param {string} texte - Le texte à tronquer
 * @param {number} longueur - La longueur maximale
 * @returns {string} Le texte tronqué
 */
export const truncateText = (texte, longueur = 100) => {
  if (!texte) return '';
  if (texte.length <= longueur) return texte;
  return texte.substring(0, longueur) + '...';
};

/**
 * Génère une couleur aléatoire
 * @returns {string} Code couleur hexadécimal
 */
export const generateRandomColor = () => {
  return '#' + Math.floor(Math.random() * 16777215).toString(16);
};

/**
 * Valide un email
 * @param {string} email - L'email à valider
 * @returns {boolean} True si valide
 */
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Valide un numéro de téléphone camerounais
 * @param {string} telephone - Le téléphone à valider
 * @returns {boolean} True si valide
 */
export const validatePhone = (telephone) => {
  if (!telephone) return false;
  const cleaned = telephone.replace(/\D/g, '');
  // Vérifie que c'est un numéro camerounais (9 chiffres après 237 ou 9 chiffres seuls)
  return /^(237)?[26]\d{8}$/.test(cleaned);
};

/**
 * Télécharge un fichier
 * @param {Blob} blob - Le blob à télécharger
 * @param {string} filename - Le nom du fichier
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Copie du texte dans le presse-papier
 * @param {string} text - Le texte à copier
 * @returns {Promise<boolean>} True si succès
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Erreur lors de la copie:', err);
    return false;
  }
};

/**
 * Formate un nom complet
 * @param {string} nom - Le nom
 * @param {string} prenom - Le prénom
 * @returns {string} Le nom complet formaté
 */
export const formatFullName = (nom, prenom) => {
  if (!nom && !prenom) return '-';
  return `${prenom || ''} ${nom || ''}`.trim();
};

/**
 * Détermine la couleur en fonction du statut
 * @param {string} statut - Le statut
 * @returns {string} La couleur correspondante
 */
export const getStatusColor = (statut) => {
  const colors = {
    ACTIF: 'success',
    INACTIF: 'error',
    EN_ATTENTE: 'warning',
    APPROUVE: 'success',
    REJETE: 'error',
    PAYE: 'success',
    IMPAYE: 'error',
    PARTIEL: 'warning',
    PLANIFIEE: 'blue',
    EN_COURS: 'processing',
    TERMINEE: 'success',
    ANNULEE: 'error',
  };
  return colors[statut] || 'default';
};

/**
 * Calcule le nombre de jours entre deux dates
 * @param {string|Date} date1 - Première date
 * @param {string|Date} date2 - Deuxième date
 * @returns {number} Nombre de jours
 */
export const daysBetween = (date1, date2) => {
  const moment = require('moment');
  return moment(date2).diff(moment(date1), 'days');
};

/**
 * Vérifie si une date est passée
 * @param {string|Date} date - La date à vérifier
 * @returns {boolean} True si la date est passée
 */
export const isPastDate = (date) => {
  const moment = require('moment');
  return moment(date).isBefore(moment(), 'day');
};

/**
 * Vérifie si une date est future
 * @param {string|Date} date - La date à vérifier
 * @returns {boolean} True si la date est future
 */
export const isFutureDate = (date) => {
  const moment = require('moment');
  return moment(date).isAfter(moment(), 'day');
};
